import { DateTime } from 'luxon';
import { Injectable } from '@angular/core';
import { AppData, AppDataFilters, AppDataSummary } from '../../models/app-data/app-data';
import { HttpClient } from '@angular/common/http';
import { DailyLogService } from '../daily-log/daily-log.service';
import { SetupService } from '../setup/setup.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { OperationService } from '../operations/operation/operation.service';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord,
  OperationFilters,
  TimeDuration,
  TypeSwitch,
  TypeSwitchGroup
} from '../../models/projects/operation.models';
import { DateService } from '../date/date.service';
import { SafetyService } from '../safety/safety.service';
import { WeatherReadingsService } from '../weather-readings/weather-readings.service';
import { ConsumablesService } from '../consumables/consumables.service';
import { VesselCrewService } from '../vessel-crew/vessel-crew.service';
import { VesselService } from '../vessel/vessel.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { JackingActivityService } from '../jacking-activity/jacking-activity.service';
import { DailyLegLoadService } from '../daily-leg-loads/daily-leg-load.service';
import { DailyObservationService } from '../daily-observation/daily-observation.service';
import { VesselOption } from '../../models/setup/setup.models';
import { FieldPositioningService } from '../field-positioning/field-positioning.service';
import { OnSiteMovementService } from '../on-site-movement/on-site-movement.service';
import { OperationTimeService } from '../operations/operation-time/operation-time.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../app/app.config';
import { UpdatedJackingDataModel } from '../../models/projects/jacking-activity.models';

@Injectable()
export class AppDataSyncService {

  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private setupService: SetupService,
    private dailyLogService: DailyLogService,
    private operationService: OperationService,
    public dateService: DateService,
    private safetyService: SafetyService,
    private weatherReadingsService: WeatherReadingsService,
    private consumablesService: ConsumablesService,
    private vesselService: VesselService,
    private vesselCrewService: VesselCrewService,
    private timeZoneService: TimeZoneService,
    private jackingActivityService: JackingActivityService,
    private dailyLegLoadService: DailyLegLoadService,
    private dailyObservationService: DailyObservationService,
    private fieldPositioningService: FieldPositioningService,
    private onSiteMovementService: OnSiteMovementService,
    private operationTimeService: OperationTimeService,
    private dateParserService: DateParserService
  ) {

  }

  async getAppData(filters: AppDataFilters): Promise<AppData> {
    filters.date = this.dateParserService.removeOffsetFromLuxonDate(filters.date);

    const url = `${this.config.apiUrl}api/appdata/day`;
    const result = await this.http
      .post<AppData>(url, filters)
      .toPromise();

    // we don't need to deserialize the returned dates here, as they are about to get saved to the local DB, so no point
    // this is only called in syncLocalData, and although that method is called many times, the returned data never needs deserializing
    return result;
  }

  async getDailyLogAppData(filters: AppDataFilters): Promise<AppData> {
    const url = `${this.config.apiUrl}api/appdata/day/dailylogdata`;

    filters.date = await this.dateParserService.removeOffsetFromLuxonDate(filters.date);

    return this.http
      .post<AppData>(url, filters)
      .toPromise()
      .then(async (data: AppData) => {
        data = await this.dateParserService.deserializeDates(data, true, this.timeZoneService);
        return data;
      });
  }

  async getNonDailyLogAppData(filters: AppDataFilters): Promise<AppData> {
    const url = `${this.config.apiUrl}api/appdata/day/nondailylogdata`;

    filters.date = await this.dateParserService.removeOffsetFromLuxonDate(filters.date);

    const result = await this.http.post<AppData>(url, filters).toPromise();

    // note that we don't both deserializing the dates here, as they don't need to be
    return result;
  }

  validateCycleProjectChange(projectId: number, date: DateTime): Promise<{canChange: boolean, message: string}> {
    const query = 'dateUtc=' + encodeURIComponent(date.toUTC().toISO()) +
      '&dateOffset=' + date.offset / 60 + (projectId == null ? '' : '&projectId=' + projectId);
    return this.http.get<{canChange: boolean, message: string}>(
      this.config.apiUrl + 'api/cycles/project-change-validation?' + query
    ).toPromise();
  }

  saveAppData(appData: AppData): Promise<AppData> {
    const url = `${this.config.apiUrl}api/appdata/save`;

    appData = this.dateParserService.removeOffsetFromLuxonDates(appData);

    return this.http
      .post<AppData>(url, appData)
      .toPromise()
      .then(async (data: AppData) => {
        // note that we don't deserialize the dates here as it's not needed
        return data;
      });
  }

  // appDataSummary.deletionData will be deleted on the API, this should really be refactored to be separate endpoints
  deleteAppData(appData: AppDataSummary): Promise<number> {
    const url = `${this.config.apiUrl}api/appdata/delete`;

    return this.http
      .post<number>(url, appData)
      .toPromise();
  }

  async getLogsForOperation(id: number): Promise<DailyOperationLogRecord[]> {
    return await this.dailyLogService.getLogsForOperation(id);
  }

  async updateAppDataForDate(
    ptLuxonDate: PtLuxonDate,
    refreshTimeZones?: boolean,
    loadWeatherLogData?: boolean,
    loadLegLoadLogData?: boolean,
    loadJackingData?: boolean
  ) {
    const vessel = await this.vesselService.getEditor();
    const filters: AppDataFilters = {
      date: ptLuxonDate.dateTime,
      vesselId: vessel ? vessel.id : null,
      loadWeatherLogData: loadWeatherLogData,
      loadLegLoadLogData: loadLegLoadLogData,
      loadJackingData: loadJackingData
    };

    await this.updateDailyLogDataForDate(filters, refreshTimeZones);
    await this.updateNonDailyLogDataForDate(filters);
  }

  // same method of updating non-dailylog data, but it only does the weather log related data, so it loads much faster
  async updateWeatherAppDataForDate(ptLuxonDate: PtLuxonDate) {
    const vessel = await this.vesselService.getEditor();
    const filters: AppDataFilters = {
      date: this.dateParserService.removeOffsetFromLuxonDate(ptLuxonDate.dateTime),
      vesselId: vessel ? vessel.id : null,
      loadWeatherLogData: null,
      loadLegLoadLogData: null,
      loadJackingData: null
    };

    const url = `${this.config.apiUrl}api/appdata/day/getweatherdata`;
    const data = await this.http.post<AppData>(url, filters).toPromise();
    // note we don't need to deserialize any dates here as they are only being saved to local storage
    await this.refreshNonDailyLogRelatedLocalData(data);
  }

  async updateDailyLogDataForDate(filters: AppDataFilters, refreshTimeZones?: boolean) {
    const dailyLogData = await this.getDailyLogAppData(filters);
    await this.refreshDailyLogRelatedLocalData(dailyLogData, refreshTimeZones);
  }

  async updateNonDailyLogDataForDate(filters: AppDataFilters) {
    const reportingDay = new PtLuxonDate(filters.date);
    const nonDailyLogData = await this.getNonDailyLogAppData(filters);
    await this.refreshNonDailyLogRelatedLocalData(nonDailyLogData);
    // Refresh after setup records, so validation uses this reporting day's stages.
    await this.operationService.refreshOperationValidationSettings(reportingDay);
  }

  async quickSaveTimeConfirmation(appData: AppData) {
    appData = await this.dateParserService.removeOffsetFromLuxonDates(appData);

    const url = `${this.config.apiUrl}api/appdata/quicksavetimeconfirmation`;

    const apiData = await this.http.post<AppData>(url, appData).toPromise();

    // note that we don't bother deserializing the dates here, as it's not needed

    await this.refreshDailyLogRelatedLocalData(apiData, false, true);
  }

  async syncLocalData(appData: AppData, minimalRefresh?: boolean, refreshTimeZones?: boolean, noDeletes?: boolean, refreshTimesToShow?: boolean): Promise<AppData> {
    const vessel = await this.vesselService.getEditor();
    appData.vesselId = vessel.id;
    let apiData = await this.saveAppData(appData);

    if (!minimalRefresh) {
      const filters: AppDataFilters = {
        date: appData.dailyLogDate ?? (await this.dailyLogService.getCurrentLogDay()).dateTime,
        vesselId: vessel ? vessel.id : null
      };

      apiData = await this.getAppData(filters);
    }

    // send apiData to be saved locally, then if needed we refresh timeShows (aka deserialize dates)
    await this.refreshLocalData(apiData, refreshTimeZones, noDeletes);

    if (refreshTimesToShow) {
      await this.refreshTimesToShow(apiData);
    }

    return apiData;
  }

  async refreshDailyLogRelatedLocalData(appData: AppData, refreshTimeZones?: boolean, noDeletes?: boolean) {
    if (appData.dailyLogRecords && appData.dailyLogRecords.length > 0) {
      if (!noDeletes) {
        await this.dailyLogService.deleteAllDailyLogData();
      }

      await this.dailyLogService.saveDailyLogData(appData.dailyLogRecords);
    }

    if (appData.dailyLogOperationInstances) {
      if (!noDeletes) {
        await this.operationService.deleteAllProjectSpecificOperationData();
      }

      await this.operationService.saveAllProjectSpecificOperationData(appData.dailyLogOperationInstances);
    }

    if (appData.timeZoneChangeEditors && appData.timeZoneChangeEditors.length > 0) {
      if (refreshTimeZones) {
        await this.timeZoneService.deleteAllTimeZoneChangeEditors();
      }

      await this.timeZoneService.saveTimeZoneChangeMultipleEditors(appData.timeZoneChangeEditors);
    }
  }

  // refreshes all non-daily log local data, i.e deletes current local data and replaces it with data from the API
  // noDeletes is used to prevent some specific data from being deleted (this is data used in app validation, which we dont delete on daily log direct editing)
  async refreshNonDailyLogRelatedLocalData(appData: AppData, noDeletes?: boolean) {
    if (appData.setupChangeLogRecords && appData.setupChangeLogRecords.length > 0) {
      await this.setupService.deleteAllSetupChangeLogRecords();
      await this.setupService.saveSetupChangeMultipleRecords(appData.setupChangeLogRecords);
    }

    if (appData.consumables) {
      await this.consumablesService.deleteAllConsumableRecords();
      await this.consumablesService.saveConsumableChangeMultipleRecords(appData.consumables);
    }

    if (appData.vesselCrewStats) {
      await this.vesselCrewService.deleteAllVesselCrewRecords();
      await this.vesselCrewService.saveVesselCrewChangeMultipleRecords(appData.vesselCrewStats);
    }

    if (appData.weatherSetups) {
      await this.weatherReadingsService.deleteAllWeatherSetupRecords();
      await this.weatherReadingsService.saveWeatherSetupMultipleRecords(appData.weatherSetups);
    }

    if (appData.weatherReadingLogRecords) {
      await this.weatherReadingsService.deleteAllWeatherReadingRecords();
      await this.weatherReadingsService.saveWeatherReadingMultipleRecords(appData.weatherReadingLogRecords);
    }

    if (appData.weatherRemarkEditors) {
      await this.weatherReadingsService.deleteAllWeatherRemarkEditors();
      await this.weatherReadingsService.saveWeatherRemarkMultipleEditors(appData.weatherRemarkEditors);
    }

    if (appData.overtimeRequestedBys) {
      await this.vesselCrewService.deleteOvertimeRequestedBys();
      await this.vesselCrewService.saveOvertimeRequestedBys(appData.overtimeRequestedBys);
    }

    if (appData.jackingActivities) {
      if (!noDeletes) {
        await this.jackingActivityService.deleteAllLocalJackingActivities();
      }

      await this.jackingActivityService.saveJackingActivitiesLocally(appData.jackingActivities);
    }

    if (appData.fieldPositioningEditors) {
      if (!noDeletes) {
        await this.fieldPositioningService.deleteAllLocalFieldPositionings();
      }

      await this.fieldPositioningService.saveFieldPositioningsLocally(appData.fieldPositioningEditors);
    }

    // delete leg loads regardless of if it's null or not - it will be null if there are no jack up activities on this day
    await this.dailyLegLoadService.deleteAllLegLoadLogs();

    if (appData.dailyLegLoads) {
      this.dailyLegLoadService.saveLegLoadMultipleRecords(appData.dailyLegLoads);
    }

    if (appData.dailyObservationStats) {
      await this.dailyObservationService.deleteAllDailyObservationRecords();
      await this.dailyObservationService.saveDailyObservationChangeMultipleRecords(appData.dailyObservationStats);
    }

    if (appData.onSiteMovementRecords) {
      await this.onSiteMovementService.deleteAllLocalOnSiteMovementRecords();
      await this.onSiteMovementService.saveOnSiteMovementRecordsLocally(appData.onSiteMovementRecords);
    }
  }

  public async refreshJackingDataForDate(date: PtLuxonDate) {
    // toMillis converts date to UTC
    const url = `${this.config.apiUrl}api/jacking/updatejackingdata/${date.dateTime.toMillis()}`;
    const res = await this.http.get<UpdatedJackingDataModel>(url).toPromise();

    if (!res) {
      return;
    }

    if (res.jackingActivityEditors && res.jackingActivityEditors.length > 0) {
      await this.jackingActivityService.deleteAllLocalJackingActivities();
      await this.jackingActivityService.saveJackingActivitiesLocally(res.jackingActivityEditors);
    }

    if (res.fieldPositioningEditors && res.fieldPositioningEditors.length > 0) {
      await this.fieldPositioningService.deleteAllLocalFieldPositionings();
      await this.fieldPositioningService.saveFieldPositioningsLocally(res.fieldPositioningEditors);
    }

    if (res.onSiteMovementEditors && res.onSiteMovementEditors.length > 0) {
      await this.onSiteMovementService.deleteAllLocalOnSiteMovementRecords();
      await this.onSiteMovementService.saveOnSiteMovementRecordsLocally(res.onSiteMovementEditors);
    }

    if (res.setupChangeLogRecords && res.setupChangeLogRecords.length > 0) {
      await this.setupService.deleteAllSetupChangeLogRecords();
      await this.setupService.saveSetupChangeMultipleRecords(res.setupChangeLogRecords);
    }
  }

  async refreshLocalData(appData: AppData, refreshTimeZones?: boolean, noDeletes?: boolean) {
    await this.refreshDailyLogRelatedLocalData(appData, refreshTimeZones, noDeletes);
    await this.refreshNonDailyLogRelatedLocalData(appData, noDeletes);
  }

  getOperationInstance(id: number) {
    const url = `${this.config.apiUrl}api/appdata/operation/${id}`;

    return this.http
      .get<DailyLogOperationInstance>(url)
      .toPromise()
      .then((data: DailyLogOperationInstance) => {
        data.startTime = PtLuxonDate.assignPtLuxonDate(data.startTime);
        data.endTime = PtLuxonDate.assignPtLuxonDate(data.endTime);
        const blob = new AppData();
        blob.dailyLogOperationInstances = [data];
        this.refreshTimesToShow(blob);
        const steps = data.stepCollectionInstance != null ? data.stepCollectionInstance.stepInstances : null;

        if (!steps) {
          return data;
        }

        steps.forEach(step => {
          step.startTime = PtLuxonDate.assignPtLuxonDate(step.startTime);
          step.endTime = PtLuxonDate.assignPtLuxonDate(step.endTime);
        });

        return data;
      });
  }

  async refreshTimesToShow(appData: AppData) {
    if (!appData.dailyLogOperationInstances) {
      return;
    }

    for (let i = 0; i < appData.dailyLogOperationInstances.length; i++) {
      appData.dailyLogOperationInstances[i] = await this.operationService.deserializeInstanceTimes(appData.dailyLogOperationInstances[i]);
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/app-data-sync/app-data-sync.service.ts